import { createTheme } from '@mui/material/styles';

// MUI theme for Round Recon — "tactical" dark UI.
//
// This is the dark, night-vision-accented design language from the product
// mockups, ported onto MUI so the whole app (public pages + dashboards/admin)
// inherits it through standard palette slots.
//
// Accessibility is still a build requirement (WCAG 2.2 AA, §16):
// - Body/secondary text colors are tuned to clear 4.5:1 against the dark
//   surfaces. `text.disabled` / `tactical.dimmer` is reserved for decorative
//   glyphs and inactive UI only — never load-bearing text.
// - High-contrast amber focus ring (visible on dark panels and the bright
//   accent) for 2.4.7 visible focus + 1.4.11 non-text contrast.
// - prefers-reduced-motion is honored; skip link retained.
//
// Contrast rationale (vs background #0a0b0d):
//   text       #ecedee  ≈ 16.8:1 (AAA)
//   dim        #9aa0ab  ≈  7.4:1 (AA, used for secondary text)
//   accent     #c8ff2c  ≈ 16.0:1 (bright night-vision green)
//   red        #ff6b6b  ≈  6.0:1   green #4cd784 ≈ 9.6:1
//   focus ring #ffbf47  non-text contrast against navy/accent/dark panels

// ── Raw design tokens ─────────────────────────────────────────────────────
const TOKENS = {
  bg: '#0a0b0d',
  bgInset: '#0e1013',
  panel: '#13151a',
  panelHi: '#1a1d23',
  line: '#23262d',
  lineHi: '#2e3239',
  text: '#ecedee',
  // dim bumped from the mockup's #8a8f99 to #9aa0ab so secondary text clears
  // AA (4.5:1) at the small sizes the design uses it.
  dim: '#9aa0ab',
  // decorative / inactive only — not for meaningful text.
  dimmer: '#6b7079',
  accent: '#c8ff2c',
  accentDim: '#7da00f',
  accentInk: '#0a0b0d',
  // red/green nudged lighter than the mockup so they pass AA as text.
  red: '#ff6b6b',
  green: '#4cd784',
} as const;

export const SANS_FONT = [
  '"Geist Sans"',
  '"Inter"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(',');

export const MONO_FONT = [
  '"Geist Mono"',
  '"JetBrains Mono"',
  'ui-monospace',
  'SFMono-Regular',
  'Menlo',
  'Consolas',
  'monospace',
].join(',');

const FOCUS_RING = '#ffbf47';
const FOCUS_OUTLINE = `3px solid ${FOCUS_RING}`;
const FOCUS_OFFSET = '2px';

// ── Custom palette slot for the raw tactical tokens ───────────────────────
// Exposed on the theme so brand components and existing pages can reach the
// panel/line/accent tones that don't map cleanly onto MUI's standard slots.
export interface TacticalPalette {
  bg: string;
  bgInset: string;
  panel: string;
  panelHi: string;
  line: string;
  lineHi: string;
  text: string;
  dim: string;
  dimmer: string;
  accent: string;
  accentDim: string;
  accentInk: string;
  red: string;
  green: string;
  monoFont: string;
  sansFont: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    tactical: TacticalPalette;
  }
  interface PaletteOptions {
    tactical?: TacticalPalette;
  }
  interface TypographyVariants {
    fontFamilyMono: string;
  }
  interface TypographyVariantsOptions {
    fontFamilyMono?: string;
  }
}

const tactical: TacticalPalette = {
  ...TOKENS,
  monoFont: MONO_FONT,
  sansFont: SANS_FONT,
};

export const theme = createTheme({
  palette: {
    mode: 'dark',
    tactical,
    primary: {
      main: TOKENS.accent,
      light: '#d8ff63',
      dark: TOKENS.accentDim,
      contrastText: TOKENS.accentInk,
    },
    // Secondary = neutral panel-toned action (matches the mockups' outlined
    // "secondary" buttons rather than introducing a second hue).
    secondary: {
      main: TOKENS.lineHi,
      light: '#3a3f48',
      dark: TOKENS.line,
      contrastText: TOKENS.text,
    },
    error: { main: TOKENS.red, light: '#ff8e8e', dark: '#c84a4a', contrastText: TOKENS.accentInk },
    warning: { main: '#ffb74d', dark: '#c98a2e', contrastText: TOKENS.accentInk },
    success: { main: TOKENS.green, dark: '#2f9a59', contrastText: '#06180e' },
    info: { main: '#5cd2f0', dark: '#2b97b8', contrastText: '#04161c' },
    divider: TOKENS.line,
    background: {
      default: TOKENS.bg,
      paper: TOKENS.panel,
    },
    text: {
      primary: TOKENS.text,
      secondary: TOKENS.dim,
      disabled: TOKENS.dimmer,
    },
  },
  typography: {
    fontFamily: SANS_FONT,
    fontFamilyMono: MONO_FONT,
    htmlFontSize: 16,
    fontSize: 16,
    h1: { fontSize: '2.25rem', fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.03em' },
    h2: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.02em' },
    h3: { fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.015em' },
    h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35 },
    body1: { fontSize: '1rem', lineHeight: 1.5 },
    body2: { fontSize: '0.9375rem', lineHeight: 1.5 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Honor user preference for reduced motion (WCAG 2.3.3).
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.001ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.001ms !important',
            scrollBehavior: 'auto !important',
          },
        },
        // Universal focus ring (keyboard users only via :focus-visible).
        'a:focus-visible, button:focus-visible, [role="button"]:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible, summary:focus-visible, [tabindex]:focus-visible':
          {
            outline: FOCUS_OUTLINE,
            outlineOffset: FOCUS_OFFSET,
          },
        // Skip link: hidden until focused (then visible at top of viewport).
        '.skip-link': {
          position: 'absolute',
          left: '-9999px',
          top: '0',
          padding: '0.75rem 1rem',
          background: TOKENS.accent,
          color: TOKENS.accentInk,
          fontWeight: 600,
          zIndex: 10_000,
          textDecoration: 'none',
          borderRadius: 8,
        },
        '.skip-link:focus, .skip-link:focus-visible': {
          left: '0.5rem',
          top: '0.5rem',
          outline: FOCUS_OUTLINE,
          outlineOffset: FOCUS_OFFSET,
        },
        body: {
          minHeight: '100vh',
          backgroundColor: TOKENS.bg,
          color: TOKENS.text,
        },
        // Placeholder contrast: MUI defaults ::placeholder to the text color at
        // opacity 0.42, which on our dark panels lands around 2.4:1 — below the
        // WCAG 2.2 AA 4.5:1 floor (#102). Pin placeholders to `dim` (#9aa0ab ≈
        // 7.4:1) at full opacity so every input's hint text is legible.
        'input::placeholder, textarea::placeholder': {
          color: TOKENS.dim,
          opacity: 1,
        },
        // Dark, slim scrollbars to match the surface.
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: TOKENS.bg },
        '*::-webkit-scrollbar-thumb': {
          background: TOKENS.lineHi,
          borderRadius: 8,
          border: `2px solid ${TOKENS.bg}`,
        },
        '*::-webkit-scrollbar-thumb:hover': { background: TOKENS.dimmer },
      },
    },
    MuiPaper: {
      styleOverrides: {
        // Kill MUI's elevation-based "lightening" overlays; we use flat panels
        // with explicit borders instead.
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:focus-visible': {
            outline: FOCUS_OUTLINE,
            outlineOffset: FOCUS_OFFSET,
          },
        },
        outlined: {
          borderColor: TOKENS.line,
          color: TOKENS.text,
          '&:hover': {
            borderColor: TOKENS.lineHi,
            backgroundColor: TOKENS.panelHi,
          },
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: FOCUS_OUTLINE,
            outlineOffset: FOCUS_OFFSET,
            borderRadius: 2,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: FOCUS_OUTLINE,
            outlineOffset: FOCUS_OFFSET,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: TOKENS.panel,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: TOKENS.line,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: TOKENS.lineHi,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
            borderColor: TOKENS.accentDim,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: TOKENS.panelHi,
          border: `1px solid ${TOKENS.line}`,
          color: TOKENS.text,
          fontSize: '0.75rem',
        },
        arrow: { color: TOKENS.panelHi },
      },
    },
  },
});

export default theme;
